import {
  ActionIcon,
  Group,
  SimpleGrid,
  Table,
  Text,
  ThemeIcon,
  UnstyledButton,
  useComputedColorScheme,
} from "@mantine/core";
import { openContextModal } from "@mantine/modals";
import {
  IconArrowsUpDown,
  IconCircleMinus,
  IconCirclePlus,
  IconFlag,
  IconSeparatorHorizontal,
  IconTrophy,
} from "@tabler/icons-react";
import { useTranslation } from "next-i18next";
import { ReactElement } from "react";
import { trackEvent } from "../../common/analytics";
import { useBoard, useIsBoardReferee } from "../../common/boards/board.hooks";
import {
  Event,
  Pool,
  PoolMember,
  TournamentType,
} from "../../common/events/event.schema";
import { Swords } from "../../common/icons/swords";
import { Match } from "../../common/matches/match.schema";
import { Member } from "../../common/members/member.schema";
import { useAuthStore } from "../../common/stores/auth.store";
import { useMatchStore } from "../../common/stores/match.store";
import RecordAvatar from "../common/RecordAvatar";
import MemberDisplay from "../members/MemberDisplay";
import classes from "./PoolTable.module.css";
import { getHasTeams, getMaxPoints } from "./util";
interface Props {
  pool: Pool;
  event: Event;
}
export interface PoolTableRow {
  pm: PoolMember;
  member: Member;
  matchCount: number;
  opponents: PoolTableOpponent[];
  index: number;
  vm: string | number;
  complete: boolean;
}
export interface PoolTableOpponent {
  match?: Match;
  self: boolean;
  points?: number;
  winner?: boolean;
  index: number;
  opponentMember?: Member;
}
export const ColumnHeader = ({
  label,
  icon,
}: {
  label: string;
  icon: ReactElement;
}) => {
  const colorScheme = useComputedColorScheme();
  return (
    <Group gap={5} justify="center">
      <ThemeIcon
        size="sm"
        color={colorScheme === "dark" ? "dark" : "gray"}
        variant="light"
      >
        {icon}
      </ThemeIcon>

      <Text visibleFrom="sm" size="xs">
        {label}
      </Text>
    </Group>
  );
};
export function getDisplayRows(pool: Pool, event: Event) {
  const activeMembers = pool.members.filter((pm) => !pm.withdrawn);
  // console.log("activeMembers :", activeMembers);
  let members = event.members as Member[];
  if (event.type === TournamentType.TEAM_POOLS_ELIMINATION)
    members = event.teams as Member[];
  // const { matches } = useMatches({
  //   eventId: event._id,
  //   options: {
  //     refetchInterval: REFETCH_INTERVAL,
  //   },
  // });
  const matches = event.matches as Match[];
  return activeMembers.map((pm, i) => {
    // console.log("pm.member :", pm.member);
    const member = members.find((m) => m._id === pm.member) as Member;
    // console.log("member :", member);
    if (!member) return null;
    let matchCount = 0;
    const opponents: PoolTableOpponent[] = [];
    activeMembers.map((opponent, oi) => {
      const self = opponent.member === pm.member;

      const opponentMember = members.find(
        (m) => m._id === opponent.member
      ) as Member;
      let match;
      let points = 0;
      let winner = true;
      if (!self) {
        match = matches.find((m) => {
          //   console.log("m ", m);
          const ms = [m.winner, m.loser]; // workaround backend not returning nested members correctly
          const found =
            m.pool &&
            ms.indexOf(member._id) > -1 &&
            ms.indexOf(opponent.member) > -1;
          //   console.log("found", found);
          if (found) return m;
          // return found
        }) as Match | undefined;
        if (match && match.complete) {
          matchCount++;
          points = match.winnerPoints as number;
          if (match.winner !== member._id) {
            winner = false;
            points = match.loserPoints as number;
          }
        }
      }
      opponents.push({
        match,
        self,
        opponentMember,
        points,
        winner,
        index: oi,
      });
    });
    const complete = matchCount === activeMembers.length - 1;
    // if (!complete) allComplete = false;
    // console.log("pm.wins :", pm.wins);
    // console.log("matchCount :", matchCount);
    // pm.wins && console.log("(pm.wins / matchCount :", pm.wins / matchCount);
    return {
      pm,
      member,
      matchCount,
      opponents,
      index: i,
      complete,
      vm: pm.wins ? (pm.wins / matchCount).toFixed(2) : 0,
      withdrawn: pm.withdrawn,
    };
  });
}
const PoolTable = ({ pool, event }: Props) => {
  // console.log("event :", event);
  const { board } = useBoard();
  const { t } = useTranslation();
  // const { classes, cx } = useStyles();
  const user = useAuthStore((s) => s.user);
  const colorScheme = useComputedColorScheme();
  const isRef = useIsBoardReferee();
  const activeMembers = pool.members.filter((pm) => !pm.withdrawn);
  const withdrawals = pool.members.filter((pm) => pm.withdrawn);
  let members = event.members as Member[];
  if (event.type === TournamentType.TEAM_POOLS_ELIMINATION)
    members = event.teams as Member[];
  const matches = event.matches as Match[];
  const endless = event.type === TournamentType.ENDLESS_POOL;
  // console.log("isRef :", isRef);
  const resetFormValues = useMatchStore((s) => s.resetFormValues);
  if (!board) return <></>;
  // console.log("members :", members);
  const openMatch = (member: Member, opponent?: Member) => {
    resetFormValues();
    let members = pool.members.map((m) => m.member);
    if (!endless)
      members = members.filter(
        (o) =>
          !matches.find((m) => {
            const ms = [m.winner, m.loser];
            return ms.indexOf(o) > -1 && ms.indexOf(member._id) > -1 && m.pool;
          })
      );
    // console.log("members :", members);
    trackEvent("open_modal_add_match", { board: board?._id });
    const hasTeams = getHasTeams(event);
    openContextModal({
      title: t("addMatch"),
      modal: "addMatch",
      fullScreen: true,
      onClose: () => resetFormValues(),
      innerProps: {
        memberId: member._id,
        members,
        opponentId: opponent?._id,
        event: event,
        requirePoints: true,
        pool: true,
        maxPoints: getMaxPoints(event, board),
        hasTeams,
      },
    });
  };
  let displayRows: (PoolTableRow | null)[] = getDisplayRows(pool, event);
  // console.log(
  //   "displayRows.filter((r) => !r?.complete).length :",
  //   displayRows.filter((r) => !r?.complete).length
  // );
  // console.log("allComplete :", allComplete);
  // console.log("displayRows :", displayRows);
  displayRows = displayRows.filter((r) => !!r);
  // console.log("displayRows :", displayRows);
  const allComplete = displayRows.filter((r) => !r?.complete).length === 0;
  return (
    <SimpleGrid cols={{ base: 1, lg: endless ? 1 : 2 }} spacing={0}>
      {!endless && (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th></Table.Th>
              <Table.Th className={classes.indexCell}></Table.Th>
              {activeMembers.map((pm, i) => {
                return (
                  <Table.Th key={i} className={classes.indexCell}>
                    {i + 1}
                  </Table.Th>
                );
              })}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {displayRows.map((row) => {
              if (!row) return;
              // console.log('member', member._id)
              const { member, index } = row;
              return (
                <Table.Tr key={member._id}>
                  <Table.Td className={classes.nameCell}>
                    <Group>
                      <RecordAvatar hiddenFrom="md" record={member} size="sm" />
                      <MemberDisplay
                        visibleFrom="md"
                        avatar
                        size="sm"
                        member={member}
                        max={20}
                        maxXS={8}
                        link
                      />
                    </Group>
                  </Table.Td>
                  <Table.Td className={classes.indexCell}>{index + 1}</Table.Td>
                  {row.opponents.map((opponent) => {
                    const {
                      self,
                      match,
                      points,
                      winner,
                      index,
                      opponentMember,
                    } = opponent;
                    const key = `${member._id}-${index}`;
                    // console.log('opponent', opponent.member)
                    if (self)
                      return (
                        <Table.Td
                          key={key}
                          className={classes.selfBout}
                        ></Table.Td>
                      );
                    // console.log('match', match)
                    if (match) {
                      if (winner) {
                        return (
                          <Table.Td key={key} className={classes.scoreCell}>
                            <ThemeIcon color="green" variant="light">
                              <Text fw={600} fz={12}>
                                V{points}
                              </Text>
                            </ThemeIcon>
                          </Table.Td>
                        );
                      } else {
                        return (
                          <Table.Td key={key} className={classes.scoreCell}>
                            <ThemeIcon color="red" variant="light">
                              <Text fw={600} fz={12}>
                                D{points}
                              </Text>
                            </ThemeIcon>
                          </Table.Td>
                        );
                      }
                    }
                    return (
                      <Table.Td key={key} className={classes.scoreCell}>
                        <UnstyledButton
                          onClick={() => openMatch(member, opponentMember)}
                        >
                          <ThemeIcon
                            color={colorScheme === "dark" ? "dark" : "gray"}
                            variant="light"
                          >
                            <Text fw={700}>-</Text>
                          </ThemeIcon>
                        </UnstyledButton>
                      </Table.Td>
                    );
                  })}
                </Table.Tr>
              );
            })}
            {withdrawals.map((row) => {
              const member = members.find((m) => m._id === row.member);
              // console.log('member', member._id)
              if (!member) return <></>;
              return (
                <Table.Tr key={member._id}>
                  <Table.Td className={classes.nameCell}>
                    <Group>
                      <RecordAvatar record={member} size="sm" />
                      <MemberDisplay
                        visibleFrom="sm"
                        size="sm"
                        member={member}
                      />
                      <ThemeIcon size="xs" color="dark" variant="light">
                        <IconFlag />
                      </ThemeIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      )}
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th hiddenFrom={endless ? undefined : "lg"}></Table.Th>
            <Table.Th>
              <ColumnHeader label="Wins" icon={<IconTrophy size={12} />} />
            </Table.Th>
            {endless && (
              <Table.Th>
                <ColumnHeader label="Matches" icon={<Swords size={12} />} />
              </Table.Th>
            )}
            {!endless && (
              <Table.Th>
                <ColumnHeader
                  label="Ratio"
                  icon={<IconArrowsUpDown size={12} />}
                />
              </Table.Th>
            )}
            <Table.Th>
              <ColumnHeader label="For" icon={<IconCirclePlus size={12} />} />
            </Table.Th>
            <Table.Th>
              <ColumnHeader
                label="Against"
                icon={<IconCircleMinus size={12} />}
              />
            </Table.Th>
            <Table.Th>
              <ColumnHeader
                label="Indicator"
                icon={<IconSeparatorHorizontal size={12} />}
              />
            </Table.Th>
            {!allComplete && isRef && (
              <Table.Th className={classes.scoreCell}></Table.Th>
            )}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {displayRows.map((row) => {
            if (!row) return;
            const { member } = row;
            const selfMember = user && user._id === member.user;
            const addSelf = selfMember && board.ownMatches;
            // console.log('member', member._id)
            return (
              <Table.Tr key={member._id}>
                <Table.Td
                  className={classes.nameCell}
                  hiddenFrom={endless ? undefined : "lg"}
                >
                  <Group>
                    <RecordAvatar visibleFrom="xs" record={member} size="sm" />
                    <MemberDisplay size="sm" member={member} max={20} />
                  </Group>
                </Table.Td>

                <Table.Td className={classes.resultCell}>
                  {row.pm.wins || 0}
                </Table.Td>

                {endless && (
                  <Table.Td className={classes.resultCell}>
                    {row.matchCount || 0}
                  </Table.Td>
                )}
                {!endless && (
                  <Table.Td className={classes.resultCell}>
                    {row.vm || 0}
                  </Table.Td>
                )}
                <Table.Td className={classes.resultCell}>
                  {row.pm.for || 0}
                </Table.Td>
                <Table.Td className={classes.resultCell}>
                  {row.pm.against || 0}
                </Table.Td>
                <Table.Td className={classes.resultCell}>
                  {row.pm.diff || 0}
                </Table.Td>
                {(endless || !allComplete) &&
                  (isRef || addSelf) &&
                  event.type !== TournamentType.TEAM_POOLS_ELIMINATION &&
                  !event.complete && (
                    <Table.Td>
                      <ActionIcon
                        size="xs"
                        variant="default"
                        onClick={() => openMatch(row.member)}
                        disabled={row.complete && !endless}
                      >
                        <Swords size={12} />
                      </ActionIcon>
                    </Table.Td>
                  )}
              </Table.Tr>
            );
          })}
          {withdrawals.map((row) => {
            const member = members.find((m) => m._id === row.member);
            // console.log('member', member._id)
            if (!member) return <></>;
            return (
              <Table.Tr key={member._id}>
                <Table.Td className={(classes.showSmall, classes.nameCell)}>
                  <Group>
                    <RecordAvatar visibleFrom="xs" record={member} size="sm" />
                    <MemberDisplay member={member} />
                    <ThemeIcon size="xs" color="dark" variant="light">
                      <IconFlag />
                    </ThemeIcon>
                  </Group>
                </Table.Td>
                <Table.Td height={40}></Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </SimpleGrid>
  );
};
export default PoolTable;
